import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackingTranslations, statusOrder } from '../utils/trackingTranslations';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Search, Package, PackageCheck, Ship, Anchor, FileCheck,
  CheckCircle2, Truck, AlertCircle, Loader2, MapPin, Calendar,
  Weight, Box, Building2, Hash, User, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusIconMap = {
  pending: Package,
  loaded: PackageCheck,
  at_sea: Ship,
  at_port: Anchor,
  customs: FileCheck,
  released: CheckCircle2,
  delivered: Truck,
  delayed: AlertCircle
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start space-x-3 p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
    <div className="w-10 h-10 rounded-lg bg-[#003d5c] flex items-center justify-center flex-shrink-0">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-base font-semibold text-[#003d5c] mt-1 break-words">{value}</p>
    </div>
  </div>
);

const ShipmentTracking = () => {
  const { language } = useLanguage();
  const t = trackingTranslations[language]?.tracking || trackingTranslations.en.tracking;
  const isRTL = language === 'ar';

  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setLoading(true);
    setError(null);
    setShipment(null);

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/shipments/track/${trackingNumber.trim()}`
      );

      if (response.data.success) {
        setShipment(response.data.shipment);
        toast.success(t.shipment_details);
      }
    } catch (err) {
      console.error('Tracking error:', err);
      if (err.response?.status === 404) {
        setError(t.not_found);
      } else {
        setError(t.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = language === 'ar' ? 'ar-SA' : language === 'tr' ? 'tr-TR' : 'en-US';
      return date.toLocaleString(locale, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateString; }
  };

  const getCurrentStatusIndex = () => {
    if (!shipment || shipment.status === 'delayed') return -1;
    return statusOrder.indexOf(shipment.status);
  };

  return (
    <section id="tracking" className="py-20 bg-gradient-to-br from-[#003d5c] via-[#004d70] to-[#003d5c] text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h2>
        </div>

        {/* Search Form */}
        <div className="max-w-3xl mx-auto mb-12">
          <form onSubmit={handleTrack} className="bg-white rounded-2xl shadow-2xl p-2 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder={t.placeholder}
                className="pl-12 h-14 text-base sm:text-lg border-0 focus:ring-0 text-gray-800"
                data-testid="tracking-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !trackingNumber.trim()}
              className="h-14 px-8 bg-[#003d5c] hover:bg-[#002a40] text-white text-base sm:text-lg font-semibold w-full sm:w-auto"
              data-testid="tracking-submit-button"
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 animate-spin mr-2" />...</>
              ) : (
                <><Search className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t.button}</>
              )}
            </Button>
          </form>
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="max-w-5xl mx-auto space-y-6" data-testid="tracking-skeleton">
            <div className="bg-white/95 rounded-2xl shadow-2xl p-8">
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-8" />
              {/* Desktop skeleton nodes */}
              <div className="hidden md:flex justify-between items-start">
                {[0,1,2,3,4,5,6].map((i) => (
                  <div key={i} className="flex flex-col items-center flex-1">
                    <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
                    <div className="mt-3 h-3 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>
              {/* Mobile skeleton rows */}
              <div className="md:hidden space-y-3">
                {[0,1,2,3,4].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-gray-50">
                    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-2 w-20 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/95 rounded-2xl shadow-2xl p-8">
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-6" />
              <div className="grid md:grid-cols-2 gap-6">
                {[0,1,2,3,4,5].map((i) => (
                  <div key={i} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="max-w-3xl mx-auto mb-8 bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center" data-testid="tracking-error">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700 font-semibold text-lg">{error}</p>
          </div>
        )}

        {/* Shipment Details */}
        {shipment && (
          <div className="max-w-5xl mx-auto space-y-6" data-testid="shipment-details">
            {/* Status Timeline */}
            <div className="bg-white text-gray-800 rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-[#003d5c] mb-8 flex items-center">
                <Clock className={`h-6 w-6 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t.status}
              </h3>

              {shipment.status === 'delayed' ? (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 flex items-center">
                  <AlertCircle className="h-12 w-12 text-orange-500 mr-4 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-orange-700">{t.statuses.delayed}</p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {/* Desktop Timeline */}
                  <div className="hidden md:flex justify-between items-start relative">
                    <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 -z-0">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-1000"
                        style={{ width: `${(getCurrentStatusIndex() / (statusOrder.length - 1)) * 100}%` }}
                      />
                    </div>
                    {statusOrder.map((status, index) => {
                      const Icon = statusIconMap[status];
                      const isActive = index <= getCurrentStatusIndex();
                      const isCurrent = index === getCurrentStatusIndex();
                      return (
                        <div key={status} className="flex flex-col items-center relative z-10 flex-1">
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                            isActive ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg scale-110' : 'bg-gray-200'
                          } ${isCurrent ? 'ring-4 ring-green-200 animate-pulse' : ''}`}>
                            <Icon className={`h-8 w-8 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                          </div>
                          <p className={`mt-3 text-xs text-center font-medium max-w-[100px] ${
                            isActive ? 'text-[#003d5c]' : 'text-gray-400'
                          }`}>
                            {t.statuses[status]}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Mobile Timeline - vertical with connecting line */}
                  <div className="md:hidden relative">
                    {/* Vertical connecting line */}
                    <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200 -z-0" />
                    <div
                      className="absolute left-6 top-6 w-0.5 bg-gradient-to-b from-green-500 to-emerald-500 transition-all duration-1000 -z-0"
                      style={{
                        height: `calc(${(getCurrentStatusIndex() / (statusOrder.length - 1)) * 100}% - 3rem)`,
                        maxHeight: 'calc(100% - 3rem)'
                      }}
                    />

                    <div className="space-y-3">
                      {statusOrder.map((status, index) => {
                        const Icon = statusIconMap[status];
                        const isActive = index <= getCurrentStatusIndex();
                        const isCurrent = index === getCurrentStatusIndex();
                        return (
                          <div key={status} className={`flex items-center gap-4 p-3 rounded-xl relative z-10 transition-all ${
                            isCurrent ? 'bg-green-50 border-2 border-green-200 shadow-md' :
                            isActive ? 'bg-green-50/50' : 'bg-gray-50'
                          }`}>
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              isActive ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg' : 'bg-gray-200'
                            } ${isCurrent ? 'ring-4 ring-green-200 scale-110' : ''}`}>
                              <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold text-sm leading-tight ${
                                isCurrent ? 'text-green-700' :
                                isActive ? 'text-[#003d5c]' : 'text-gray-400'
                              }`}>
                                {t.statuses[status]}
                              </p>
                              {isCurrent && (
                                <p className="text-xs text-green-600 mt-0.5 font-medium">
                                  ● {language === 'ar' ? 'الحالة الحالية' : language === 'tr' ? 'Şu anki durum' : 'Current status'}
                                </p>
                              )}
                            </div>
                            {isActive && !isCurrent && (
                              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Current Status Highlight */}
                  <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{t.status}</p>
                    <p className="text-2xl font-bold text-green-700">{t.statuses[shipment.status]}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Shipment Info Grid */}
            <div className="bg-white text-gray-800 rounded-2xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-[#003d5c] mb-6">{t.shipment_details}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <InfoRow icon={Hash} label={t.shipment_number} value={shipment.tracking_number} />
                {shipment.container_number && <InfoRow icon={Box} label={t.container_number} value={shipment.container_number} />}
                {shipment.booking_number && <InfoRow icon={Hash} label={t.booking_number} value={shipment.booking_number} />}
                {shipment.customer_name && <InfoRow icon={User} label={language === 'ar' ? 'العميل' : language === 'tr' ? 'Müşteri' : 'Customer'} value={shipment.customer_name} />}
                {shipment.origin && <InfoRow icon={MapPin} label={t.origin} value={shipment.origin} />}
                {shipment.destination && <InfoRow icon={MapPin} label={t.destination} value={shipment.destination} />}
                {shipment.carrier && <InfoRow icon={Building2} label={t.carrier} value={shipment.carrier} />}
                {shipment.weight && <InfoRow icon={Weight} label={t.weight} value={shipment.weight} />}
                {shipment.volume && <InfoRow icon={Box} label={t.volume} value={shipment.volume} />}
                {shipment.est_departure && <InfoRow icon={Calendar} label={t.est_departure} value={shipment.est_departure} />}
                {shipment.est_arrival && <InfoRow icon={Calendar} label={t.est_arrival} value={shipment.est_arrival} />}
              </div>
            </div>

            {/* Status History */}
            {shipment.status_history && shipment.status_history.length > 0 && (
              <div className="bg-white text-gray-800 rounded-2xl shadow-2xl p-8">
                <h3 className="text-2xl font-bold text-[#003d5c] mb-6">{t.history}</h3>
                <div className="space-y-4">
                  {[...shipment.status_history].reverse().map((update, index) => {
                    const Icon = statusIconMap[update.status] || Package;
                    return (
                      <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#003d5c] to-[#005580] flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-[#003d5c]">{t.statuses[update.status] || update.status}</p>
                          {update.location && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />{update.location}
                            </p>
                          )}
                          {update.notes && <p className="text-sm text-gray-500 mt-1 italic">{update.notes}</p>}
                          <p className="text-xs text-gray-400 mt-2">{formatDate(update.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ShipmentTracking;
